import React, { ReactNode, forwardRef, useEffect, useState, useCallback, Children, useRef } from "react";

import { Box, ButtonBase, Divider, Skeleton, Stack, StackProps } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { ArrowForwardOutlined, ExpandLess, ExpandMore } from "@mui/icons-material";
import { DispatchType, useLocalStorage } from "@bridgesplit/react";

import { BORDER_RADIUS, DIALOG_WINDOW_CENTER_MIN_HEIGHT, FONT_SIZES, SPACING, useAppPalette } from "../theme";
import { MediaQuery, SxType } from "../types";
import { MEDIA } from "../util";
import { Text, TextColor, TextSkeleton, TextVariant } from "./typography";
import { ToggleSpacedButtons } from "./buttons";
import { SelectOption } from "./input";
import { Tooltip } from "./misc";
import { hideScrollbarSx } from "../constants";

interface ColumnProps extends StackProps {
    handleHovered?: DispatchType<boolean>;
}

export const Column = forwardRef<HTMLDivElement, ColumnProps>(function Column(
    { handleHovered, spacing, sx, ...props },
    ref
) {
    const negativeSpacing = spacing && typeof spacing === "number" && spacing < 0;
    return (
        <Stack
            ref={ref}
            spacing={negativeSpacing ? spacing : undefined}
            id="column"
            sx={{ gap: negativeSpacing ? undefined : spacing, ...sx }}
            onMouseEnter={() => handleHovered?.(true)}
            onMouseLeave={() => handleHovered?.(false)}
            {...props}
        />
    );
});

interface RowProps extends ColumnProps {
    spaceBetween?: boolean;
    wrap?: boolean;
    loading?: boolean;
}
export const Row = forwardRef<HTMLDivElement, RowProps>(
    ({ spaceBetween, handleHovered, wrap, sx, spacing, loading, children, ...props }, ref) => {
        return (
            <Stack
                ref={ref}
                onMouseEnter={() => handleHovered?.(true)}
                onMouseLeave={() => handleHovered?.(false)}
                id="row"
                sx={{
                    gap: spacing,
                    justifyContent: spaceBetween ? "space-between" : undefined,
                    alignItems: props.alignItems ?? "center",
                    flexWrap: wrap ? "wrap" : undefined,
                    ...sx
                }}
                direction="row"
                {...props}
            >
                {loading ? <RowLoadingSkeleton /> : children}
            </Stack>
        );
    }
);

function RowLoadingSkeleton() {
    return (
        <Skeleton
            variant="rectangular"
            sx={{ width: "100%", height: SPACING * 6 + "px", borderRadius: BORDER_RADIUS + "px" }}
        />
    );
}

export function MediaWrapper({ query, children }: { query: string; children: ReactNode }) {
    return <Box sx={{ display: "none", [query]: { display: "initial" } }}> {children} </Box>;
}

interface MediaStackProps extends StackProps {
    query?: MediaQuery;
    spacing?: number;
    mobileSpacing?: number;
    spaceBetweenRow?: boolean;
    sxAbove?: SxType;
    sxBelow?: SxType;
}
export function MediaStack({
    query = MEDIA.LG,
    children,
    spacing = 1,
    mobileSpacing,
    spaceBetweenRow,
    sxAbove,
    sxBelow,
    sx,
    ...otherProps
}: MediaStackProps) {
    return (
        <Stack
            sx={{
                [query.below]: {
                    flexDirection: "column",
                    gap: `${SPACING * (mobileSpacing ?? (spacing || 0))}px`,
                    ...sxBelow
                },
                [query.above]: {
                    justifyContent: spaceBetweenRow ? "space-between" : undefined,
                    alignItems: "center",
                    flexDirection: "row",
                    gap: `${SPACING * (spacing || 0)}px`,
                    ...sxAbove
                },

                ...sx
            }}
            {...otherProps}
        >
            {children}
        </Stack>
    );
}

export function SectionedCard({
    children,
    padding = 2,
    title,
    titleVariant = "h4",
    showTitleSection,
    sx,
    spacing = 1.5
}: {
    children: ReactNode;
    padding?: number;
    title?: string;
    titleVariant?: TextVariant;
    showTitleSection?: boolean;
    sx?: SxType;
    spacing?: number;
}) {
    const { cardBackground, border } = useAppPalette();
    const [firstChild, ...remainingChildren] = Children.toArray(children);

    return (
        <Column
            spacing={spacing}
            sx={{
                background: cardBackground,
                border: border,
                borderRadius: BORDER_RADIUS,
                py: padding,
                ...sx
            }}
        >
            {title && (
                <>
                    <Box px={padding}>
                        <Text variant={titleVariant}> {title}</Text>
                    </Box>
                    {showTitleSection && <Divider />}
                </>
            )}

            <Box px={padding}>{firstChild}</Box>

            {remainingChildren.length > 0 && (
                <>
                    <Divider />
                    <Column px={padding}>{remainingChildren}</Column>
                </>
            )}
        </Column>
    );
}

export function OutlinedCard({
    children,
    padding = 2,
    sx,
    spacing,
    hideCard,
    hoverable,
    growOnHover,

    backgroundColor,
    hideBorder
}: {
    children: ReactNode;
    padding?: number;
    sx?: SxType;
    spacing?: number;
    hideCard?: boolean;
    hoverable?: boolean;
    growOnHover?: boolean;
    backgroundColor?: string;
    hideBorder?: boolean;
}) {
    const { border, cardBackground, hoverBackground } = useAppPalette();
    return (
        <Column
            spacing={spacing}
            sx={
                hideCard
                    ? sx
                    : {
                          ":hover": {
                              background: hoverable && !growOnHover ? hoverBackground : undefined,
                              transform: hoverable && growOnHover ? "scale(1.01)" : undefined,
                              transition: "background 0.2s ease, transform 0.2s ease"
                          },
                          transition: "background 0.2s ease, transform 0.3s ease",
                          border: hideBorder ? undefined : border,
                          background: backgroundColor ?? cardBackground,
                          borderRadius: BORDER_RADIUS,
                          p: padding,
                          ...sx
                      }
            }
        >
            {children}
        </Column>
    );
}

export function OutlinedCardWithBanner({
    children,
    padding = 2,
    sx,
    spacing,
    hideCard,
    hoverable,
    banner
}: {
    children: ReactNode;
    padding?: number;
    sx?: SxType;
    spacing?: number;
    hideCard?: boolean;
    hoverable?: boolean;
    banner: ReactNode;
}) {
    const { border, cardBackground, hoverBackground } = useAppPalette();
    return (
        <Column
            spacing={spacing}
            sx={
                hideCard
                    ? sx
                    : {
                          ":hover": { background: hoverable ? hoverBackground : undefined },
                          border,
                          background: cardBackground,
                          borderRadius: BORDER_RADIUS,
                          overflow: "hidden",
                          ...sx
                      }
            }
        >
            {banner}
            <Column spacing={spacing} padding={padding} sx={{ p: padding }}>
                {children}
            </Column>
        </Column>
    );
}

export function ShadowCard({
    children,
    spacing,
    sx,
    onClick,
    padding,
    handleHovered
}: {
    children: React.ReactNode;
    sx?: SxType;
    spacing?: number;
    onClick?: () => void;
    padding?: boolean;
    handleHovered?: DispatchType<boolean>;
}) {
    const { cardBackground, border } = useAppPalette();
    return (
        <Column
            onClick={onClick}
            handleHovered={handleHovered}
            spacing={spacing}
            sx={{
                p: padding ? "20px" : undefined,
                overflow: "hidden",
                borderRadius: BORDER_RADIUS,
                backgroundColor: cardBackground,
                border,
                ...sx
            }}
        >
            {children}
        </Column>
    );
}
type SplitLayoutProps = {
    left: React.ReactNode;
    right: React.ReactNode;
    ratio?: [number, number];
    gap?: number;
};

export function SplitLayout({ left, right, ratio = [3, 1], gap = 2 }: SplitLayoutProps) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: `${ratio[0]}fr ${ratio[1]}fr`,
                gap,
                width: "100%",
                alignItems: "stretch",
                height: "100%",
                minHeight: 0,

                "@media (max-width: 900px)": {
                    gridTemplateColumns: "1fr"
                }
            }}
        >
            <Box sx={{ minWidth: 0 }}>{left}</Box>
            <Box sx={{ minWidth: 0 }}>{right}</Box>
        </Box>
    );
}
export function DynamicGrid({
    children,
    minImageWidth = "200px",
    gridGap = 2,
    sx
}: {
    children: ReactNode;
    minImageWidth?: string;
    sx?: SxType;
    gridGap?: number;
}) {
    return (
        <Stack
            sx={{
                display: "grid",
                gridGap: `${gridGap * SPACING}px`,
                gridTemplateColumns: `repeat(auto-fill, minmax(${minImageWidth}, 1fr))`,
                ...sx
            }}
        >
            {children}
        </Stack>
    );
}

export function StaticGrid({
    children,
    columns,
    mobileColumns = 1,
    query = MEDIA.MD,
    sx,
    gridGap = 2
}: {
    children: ReactNode;
    mobileColumns?: number;
    columns: number;
    query?: MediaQuery;
    sx?: SxType;
    gridGap?: number;
}) {
    return (
        <Stack
            sx={{
                display: "grid",
                gridGap: `${gridGap * SPACING}px`,
                [query.above]: {
                    gridTemplateColumns: Array(columns).fill("1fr").join(" ")
                },
                [query.below]: {
                    gridTemplateColumns: Array(mobileColumns).fill("1fr").join(" ")
                },

                ...sx
            }}
        >
            {children}
        </Stack>
    );
}

export interface HeaderCardProps {
    header: ReactNode;
    headerVariant?: TextVariant;
    children: React.ReactNode;
    wrapperSx?: SxType;
    childrenSx?: SxType;
    spacing?: number;
    loading?: boolean;
    headerSpacing?: number;
    showDivider?: boolean;
}

export function HeaderCard({
    header,
    headerVariant = "h3",
    headerSpacing = 2,
    wrapperSx,
    children,
    childrenSx,
    spacing,
    loading,
    showDivider = true
}: HeaderCardProps) {
    return (
        <Column spacing={headerSpacing} id="header-card" sx={{ width: "100%", ...wrapperSx }}>
            {typeof header === "string" ? (
                <Text loading={loading} variant={headerVariant}>
                    {header}
                </Text>
            ) : (
                header
            )}
            {showDivider && <Divider />}
            <Column spacing={spacing} id="header-body" sx={{ ...childrenSx }}>
                {children}
            </Column>
        </Column>
    );
}

export interface TabInterface {
    label: React.ReactNode;
    component: React.ReactNode;
    icon?: React.ReactNode;
    hide?: boolean;
    disabled?: boolean; // renders the tab greyed out and non-clickable
    tooltip?: React.ReactNode; // shown on hover (e.g. why a tab is disabled)
    id?: string; // use for params
}
type Props = {
    tabs: TabInterface[];
    bodySx?: SxType;
    labelSx?: SxType;
    tabSx?: SxType;
    wrapperSx?: SxType;
    spacing?: number;
    childrenSpacing?: number;
    variant?: "standard" | "spaced-out" | "toggle-buttons";
    tabId?: string;
    paramsOptions?: { allowed: boolean; overrideExistingParams?: boolean };
    rowEndElement?: ReactNode;
    textVariant?: TextVariant;
    onTabClick?: () => void;
    loading?: boolean;
    localStorageKey?: string;
};

export function TabsCard({
    tabs: propTabs,
    bodySx,
    labelSx,
    tabSx,
    wrapperSx,
    spacing = 2,
    childrenSpacing,
    paramsOptions,
    rowEndElement,
    textVariant = "h3",
    onTabClick,
    variant = "standard",
    loading,
    localStorageKey
}: Props) {
    const tabs = propTabs.filter((t) => !t.hide);
    const [params, updateParams] = useSearchParams();
    const open = params.get("open");
    const openIndex = tabs.findIndex((t, i) => !t.disabled && (t.id ?? i.toString()) === open);
    const tabIndex = !paramsOptions?.allowed || openIndex === -1 ? undefined : openIndex;

    const localStorageState = useLocalStorage(localStorageKey ?? "", tabIndex || 0);
    const localState = React.useState<number>(tabIndex || 0);
    const [openTab, setOpenTab] = localStorageKey ? localStorageState : localState;

    // Never resolve to a disabled tab (e.g. stale localStorage/URL pointing at a now-disabled tab).
    // If every tab is disabled (e.g. data still loading), fall back to the first tab so the body
    // never renders empty from a -1 index.
    const firstEnabledIndex = tabs.findIndex((t) => !t.disabled);
    const selectedIndex =
        openTab < tabs.length && !tabs[openTab]?.disabled ? openTab : firstEnabledIndex === -1 ? 0 : firstEnabledIndex;
    const selectedTab = tabs[selectedIndex] ?? tabs[0];
    useEffect(() => {
        if (tabIndex !== undefined && tabIndex !== openTab) {
            setOpenTab(tabIndex);
        }
    }, [openTab, setOpenTab, tabIndex]);

    const handleTabClick = useCallback(
        (tab: TabInterface, index: number) => {
            setOpenTab(index);
            onTabClick?.();
            if (paramsOptions?.allowed) {
                const tabId = tabs[index].id ?? index.toString();
                if (paramsOptions.overrideExistingParams) {
                    updateParams({ open: tabId });
                } else {
                    params.set("open", tabId);
                    updateParams(params);
                }
            }
        },
        [
            setOpenTab,
            onTabClick,
            paramsOptions?.allowed,
            paramsOptions?.overrideExistingParams,
            tabs,
            updateParams,
            params
        ]
    );

    // edge case
    if (tabs.length === 0) return null;

    if (loading) {
        return (
            <HeaderCard header={<TextSkeleton variant={textVariant} />} spacing={spacing}>
                <Column spacing={childrenSpacing} sx={bodySx}>
                    {selectedTab.component}
                </Column>
            </HeaderCard>
        );
    }

    return (
        <Column sx={wrapperSx} spacing={spacing}>
            {variant === "toggle-buttons" && (
                <ToggleSpacedButtons
                    containerSx={{ overflowX: "auto", ...hideScrollbarSx }}
                    options={tabs.map((t, i) => ({ value: i, label: t.label }))}
                    value={selectedIndex}
                    setValue={(index) => handleTabClick(tabs[index], index)}
                />
            )}
            {variant !== "toggle-buttons" && (
                <TabWrapper sx={tabSx}>
                    {tabs.map((tab, i) => {
                        const tabNode = (
                            <CustomTab
                                key={i}
                                hideStyles={tabs.length === 1}
                                textVariant={textVariant}
                                index={i}
                                wrapperSx={
                                    variant === "spaced-out" ? { width: "100%", alignItems: "center", pt: 1.5 } : {}
                                }
                                labelSx={{ paddingLeft: i === 0 ? 0 : undefined, ...labelSx }}
                                onClick={tab.disabled ? undefined : () => handleTabClick(tab, i)}
                                icon={tab.icon}
                                label={tab.label}
                                isSelected={selectedIndex === i}
                                disabled={tab.disabled}
                            />
                        );
                        return tab.tooltip ? (
                            <Tooltip key={i} title={tab.tooltip}>
                                {tabNode}
                            </Tooltip>
                        ) : (
                            tabNode
                        );
                    })}
                    {/* last "tab" becomes rest of border */}
                    {variant === "standard" && <TabDivider />}
                    {rowEndElement ? (
                        <CustomTab textVariant={textVariant} index={-1} label={rowEndElement} isSelected={false} />
                    ) : null}
                </TabWrapper>
            )}
            <Column spacing={childrenSpacing} sx={bodySx}>
                {selectedTab.component}
            </Column>
        </Column>
    );
}

export function TabsSelect<T extends string | number>({
    value,
    setValue,
    options,
    wrapperSx,
    textVariant = "h4",
    tabSx,
    hideUnselectedDivider = false
}: {
    options: SelectOption<T>[];
    value: T;
    setValue: DispatchType<T>;
    wrapperSx?: SxType;
    textVariant?: TextVariant;
    tabSx?: SxType;
    hideUnselectedDivider?: boolean;
}) {
    return (
        <TabWrapper sx={wrapperSx}>
            {options.map((tab, i) => (
                <CustomTab
                    hideStyles={options.length === 1}
                    textVariant={textVariant}
                    index={i}
                    wrapperSx={{ width: "100%", alignItems: "center", pt: 1.5, ...tabSx }}
                    onClick={() => {
                        setValue(tab.value as T);
                    }}
                    label={tab.label ?? tab.value}
                    isSelected={tab.value === value}
                    key={i}
                    hideUnselectedDivider={hideUnselectedDivider}
                />
            ))}
        </TabWrapper>
    );
}

function TabWrapper({ children, sx }: { children: ReactNode; sx?: SxType }) {
    return (
        <Stack
            role="tablist"
            aria-orientation="horizontal"
            sx={{
                flexDirection: "row",
                alignItems: "flex-end",
                width: "100%",
                maxWidth: "100vw",
                overflow: "auto",
                "::-webkit-scrollbar ": { display: "none" },
                scrollbarWidth: "none",
                ...sx
            }}
        >
            {children}
        </Stack>
    );
}

export function TabDivider() {
    return (
        <CustomTab
            index={-1}
            wrapperSx={{ width: "100%" }}
            label={<Stack sx={{ width: "100%" }} />}
            isSelected={false}
        />
    );
}

export function CustomTab({
    label,
    isSelected,
    onClick,
    labelSx,
    index,
    icon,
    wrapperSx,
    textVariant = "h3",
    hideStyles,
    hideUnselectedDivider,
    disabled
}: {
    label: React.ReactNode;
    icon?: React.ReactNode;
    isSelected: boolean;
    onClick?: () => void;
    labelSx?: SxType;
    wrapperSx?: SxType;
    index: number;
    textVariant?: TextVariant;
    hideStyles?: boolean;
    hideUnselectedDivider?: boolean;
    disabled?: boolean;
}) {
    const { textPrimary, hoverBackground } = useAppPalette();

    return (
        <ButtonBase
            disableRipple
            aria-disabled={disabled}
            tabIndex={index === -1 || disabled ? -1 : 0}
            role="tab"
            sx={{
                display: "flex",
                flexDirection: "column",
                borderRadius: BORDER_RADIUS,
                gap: ["h4", "body1", "body2"].includes(textVariant) ? 1 : 2,
                pt: 0.5,
                ":focus-visible": { outline: "none", background: index === -1 ? "none" : hoverBackground },
                cursor: !disabled && onClick && !hideStyles ? "pointer" : "default",
                opacity: disabled ? 0.5 : 1,
                minWidth: "fit-content",
                ...wrapperSx
            }}
            onClick={onClick}
        >
            {typeof label === "string" ? (
                <Text sx={{ px: 2, ...labelSx }} color={isSelected ? "body" : "disabled"} variant={textVariant}>
                    {icon}
                    {label}
                </Text>
            ) : (
                label
            )}
            <Divider
                sx={{
                    borderColor:
                        isSelected && !hideStyles ? textPrimary : hideUnselectedDivider ? "transparent" : undefined
                }}
                flexItem
            />
        </ButtonBase>
    );
}

export function AbsoluteCenterWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Stack
            sx={{
                position: "fixed",
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
                justifyContent: "center",
                alignItems: "center"
            }}
        >
            {children}
        </Stack>
    );
}

interface VerticalScrollProps extends StackProps {
    maxHeight: number;
    maxHeightMobile?: string;
}
export const VerticalScroll = forwardRef<HTMLDivElement, VerticalScrollProps>(
    ({ sx, maxHeight, children, spacing = 0.5, maxHeightMobile = "30vh", ...props }, ref) => {
        return (
            <Stack
                id="vertical-scroll"
                ref={ref}
                sx={{
                    maxHeight: `${maxHeight}px`,
                    [`@media (max-height: ${DIALOG_WINDOW_CENTER_MIN_HEIGHT}px)`]: {
                        maxHeight: maxHeightMobile
                    },
                    minHeight: "100%",
                    overflowY: "auto",
                    "::-webkit-scrollbar ": { display: "none" },
                    scrollbarWidth: "none",
                    gap: spacing,
                    ...sx
                }}
                {...props}
            >
                {children}
            </Stack>
        );
    }
);

export function ExpandContent({
    children,
    sx,
    headerSx,
    header,
    textVariant,
    textColor,
    defaultExpanded = false,
    hideExpand = false,
    spacing = 1
}: {
    header: string | JSX.Element;
    children: ReactNode;
    sx?: SxType;
    headerSx?: SxType;
    textVariant?: TextVariant;
    textColor?: TextColor;
    defaultExpanded?: boolean;
    hideExpand?: boolean;
    spacing?: number;
}) {
    const [expanded, setExpanded] = useState<boolean>(defaultExpanded);

    if (hideExpand) {
        return (
            <Stack spacing={spacing} sx={sx}>
                <Text variant={textVariant} color={textColor}>
                    {header}
                </Text>
                {children}
            </Stack>
        );
    }

    return (
        <Stack spacing={spacing} sx={sx}>
            <Row
                sx={{ cursor: "pointer", ...headerSx }}
                spaceBetween
                onClick={() => setExpanded(!expanded)}
                spacing={1}
            >
                {typeof header === "string" ? (
                    <Text variant={textVariant} color={textColor}>
                        {header}
                    </Text>
                ) : (
                    header
                )}
                <Text variant={textVariant} color={textColor}>
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                </Text>
            </Row>

            {expanded ? children : null}
        </Stack>
    );
}

export function NestedCard({
    children,
    position = "bottom",
    sx
}: {
    children: ReactNode;
    position?: "top" | "bottom" | "left" | "right";
    sx?: SxType;
}) {
    const { cardBackground } = useAppPalette();
    const borderRadius = [
        position === "top" || position === "left" ? BORDER_RADIUS : "0px",
        position === "top" || position === "right" ? BORDER_RADIUS : "0px",
        position === "bottom" || position === "right" ? BORDER_RADIUS : "0px",
        position === "bottom" || position === "left" ? BORDER_RADIUS : "0px"
    ].join(" ");
    return (
        <Row
            spaceBetween
            sx={{
                borderRadius,
                backgroundColor: cardBackground,
                p: 2,
                ...sx
            }}
        >
            {children}
        </Row>
    );
}

export function ArrowDivider() {
    const { cardBackground, paperBackground, textDisabled } = useAppPalette();
    const size = "20px";
    return (
        <Row sx={{ width: "2px", justifyContent: "center" }}>
            <Box sx={{ background: paperBackground, p: "2px", zIndex: 1, borderRadius: "100%" }}>
                <Box
                    sx={{
                        background: cardBackground,
                        svg: { fontSize: FONT_SIZES.body1, color: textDisabled },
                        height: size,
                        width: size,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "100%"
                    }}
                >
                    <ArrowForwardOutlined />
                </Box>
            </Box>
        </Row>
    );
}
